import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpRequest } from "../../../HttpRequest";
import { useDispatch } from "react-redux";
import { logInUser } from "../../../../features/userSlice";
import { setAdditionalServices, setBusinessName, setEvoucherEnabled } from "../../../../features/configsSlice";
import secureLocalStorage from "react-secure-storage";
import { setBanners, setButtons, setColors, setDisclaimer, setLogo, setPreOrderBadgeType, setTemplate, setTypography } from "../../../../features/template";
import { Box, CircularProgress, Typography } from "@mui/material";
import zedLogo from "../../../../images/zedLogo.png";

const zedUrl = process.env.REACT_APP_ZED_URL;

const AdminLogin = () => {
    const [fetchStatus, setFetchStatus] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState("Authenticating...");

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const fetchAdditionalServicesConfigs = () => {
        HttpRequest({
            method: 'GET',
            url: `/api/v1/ecommerce/get_additional_services_status`,
        }).then((data) => {
            if (data.status === 200) {
                const payload = data.response.data;
                dispatch(setAdditionalServices(payload));
            } 
        }).catch((error) => {
        });
    };

    const getBusinessInfo = () => { // 
        HttpRequest({
          method: 'POST',
          url: `/api/get_business_info`,
        }).then((data) => {
          if (data?.status === 200) {
            dispatch(setBusinessName(data?.response?.data?.businessName));
            dispatch(setEvoucherEnabled(data?.response?.data?.isEvoucherEnabled));
            const templateData = data?.response?.data?.ecommerceTemplateConfig;
            dispatch(setTemplate(templateData?.template));
            dispatch(setLogo(templateData?.logo));
            dispatch(setColors(templateData?.colors));
            dispatch(setTypography(templateData?.typography));
            dispatch(setButtons(templateData?.buttons));
            dispatch(setBanners(templateData?.banner));
            dispatch(setPreOrderBadgeType(templateData?.preOrderBadgeType));
            dispatch(setDisclaimer(templateData?.disclaimer));
          }
        }).catch((error) => {
        });
    };

    useEffect(() => {
        const loginParams = new URLSearchParams(location.search);

        const businessId = loginParams.get('businessId');
        const businessNumber = loginParams.get('businessNumber');
        const businessCategory = loginParams.get('businessCat');
        const userId = loginParams.get('userId');

        if (businessId !== null && businessNumber !== null && businessCategory === 'ZED_ECOMMERCE' && userId !== null) {
            let loginPayload = {
                "businessNumber": businessNumber,
                "businessId": businessId,
                "userId": userId,
                "branchId": ""
            };
            HttpRequest({
                method: 'POST',
                url: `/api/v1/ecommerce/zed_ecommerce_login`,
                body: loginPayload
            }).then((data) => {
                if (data.status === 200) {
                    setLoadingText("Verifying credentials...");
                    secureLocalStorage.setItem("group", "admin");
                    secureLocalStorage.setItem("token", data.response.data.token);
                    secureLocalStorage.setItem("businessId", data.response.data.businessNumber);
                    setFetchStatus(true);
                    dispatch(logInUser({ isLoggedIn: true }));
                    setLoadingText("Loading dashboard settings...");
                    fetchAdditionalServicesConfigs();
                    getBusinessInfo();

                    // Add a small delay to show the final message before navigation
                    setTimeout(() => {
                        setLoading(false);
                        navigate('/zed_ecommerce/dashboard');
                    }, 500);
                } else {
                    setLoading(false);
                    window.location.href = zedUrl;
                }
            }).catch((error) => {
                setLoading(false);
                window.location.href = zedUrl;
            });
        } else {
            setLoading(false);
            window.location.href = zedUrl;
        }
    }, [location.search]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100%',
                backgroundColor: '#f5f5f5'
            }}
        >
            {loading && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3,
                        backgroundColor: '#fff',
                        padding: '60px 80px',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(3, 37, 65, 0.1)',
                        animation: 'fadeIn 0.5s ease-in',
                        '@keyframes fadeIn': {
                            from: {
                                opacity: 0,
                                transform: 'translateY(-20px)'
                            },
                            to: {
                                opacity: 1,
                                transform: 'translateY(0)'
                            }
                        }
                    }}
                >
                    <img
                        src={zedLogo}
                        alt="Zed Logo"
                        style={{
                            width: '150px',
                            marginBottom: '20px',
                            animation: 'pulse 2s ease-in-out infinite'
                        }}
                    />
                    <style>
                        {`
                            @keyframes pulse {
                                0%, 100% {
                                    transform: scale(1);
                                    opacity: 1;
                                }
                                50% {
                                    transform: scale(1.05);
                                    opacity: 0.9;
                                }
                            }
                        `}
                    </style>
                    <CircularProgress
                        size={60}
                        sx={{
                            color: '#032541'
                        }}
                    />
                    <Typography
                        variant="h6"
                        sx={{
                            color: '#032541',
                            fontFamily: 'Poppins',
                            fontWeight: 500,
                            fontSize: '18px',
                            marginTop: '16px',
                            textAlign: 'center'
                        }}
                    >
                        {loadingText}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#707070',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontSize: '14px',
                            marginTop: '8px',
                            textAlign: 'center'
                        }}
                    >
                        Please wait while we prepare your dashboard
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default AdminLogin;
